/**
 * InputBar — chat input control + quick-start chips.
 *
 * Pure presentation: calls `onSend(content)` on submit. The parent owns
 * streaming state via useChat (Task 7) and passes `isStreaming` so the
 * input disables during a turn.
 *
 * Quick-start chips render above the input. Clicking a chip submits its
 * `prompt` immediately (no need to type). Phase 5+ wires chips dynamically
 * from role.yaml; K.4 hardcodes them at the route level.
 */
import { useState, type FormEvent } from 'react';

export interface InputBarChip {
  label: string;
  prompt: string;
  /** K.5 — when set, chip click triggers cell render via parent. */
  cellHint?: string;
}

export interface InputBarProps {
  onSend: (content: string) => void;
  isStreaming: boolean;
  /** Quick-start chips displayed above the input. */
  chips?: ReadonlyArray<InputBarChip>;
  /** K.5 — optional override for chip click handling. When provided, the
   *  parent owns chip-click routing (e.g. cell render vs send via /chat).
   *  When absent, chip click defaults to onSend(chip.prompt) — the K.4 behavior. */
  onChipClick?: (chip: InputBarChip) => void;
  /** Optional placeholder; defaults to a generic prompt. */
  placeholder?: string;
}

export function InputBar({
  onSend,
  isStreaming,
  chips = [],
  onChipClick,
  placeholder = 'Ask about your investments, KYC status, or offerings…',
}: InputBarProps) {
  const [value, setValue] = useState('');

  const submit = (e?: FormEvent) => {
    e?.preventDefault();
    const trimmed = value.trim();
    if (!trimmed || isStreaming) return;
    onSend(trimmed);
    setValue('');
  };

  return (
    <div className="border-t border-border bg-background px-4 py-3">
      {chips.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2">
          {chips.map((chip) => (
            <button
              key={chip.label}
              type="button"
              onClick={() => {
                if (isStreaming) return;
                if (onChipClick) onChipClick(chip);
                else onSend(chip.prompt);
              }}
              disabled={isStreaming}
              className="px-3 py-1 text-xs rounded-full border border-border hover:bg-card disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {chip.label}
            </button>
          ))}
        </div>
      )}
      <form onSubmit={submit} className="flex gap-2">
        <input
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={placeholder}
          disabled={isStreaming}
          className="flex-1 rounded-md border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
          aria-label="Chat message input"
        />
        <button
          type="submit"
          disabled={isStreaming || !value.trim()}
          className="rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Send
        </button>
      </form>
    </div>
  );
}
